'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import LightGallery from 'lightgallery/react';

import videojs from 'video.js';

import { usePostHog } from '@posthog/react';

import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';
import { useCookieConsentStatus } from '@hub/common-components/src/CookieConsentStatusProvider';
import type { AfterSlideDetail, BeforeSlideDetail, InitDetail } from 'lightgallery/lg-events';
import type { LightGallery as LightGalleryType } from 'lightgallery/lightgallery';

import { CitOrigin, MediaAspectRatio, type Media } from '@hub/common/src/types/cit-public-api';

// Plugins
import lgVideo from 'lightgallery/plugins/video';
import lgZoom from 'lightgallery/plugins/zoom';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgFullscreen from 'lightgallery/plugins/fullscreen';

import {
  isImage,
  isPDF,
  getOriginalPDFUrl,
  isVideo,
  getThumbnailPath,
  getDerivativeVideoPath,
  isAudio,
  getDerivativeAudioPath,
  getPosterPath,
  getDownloadUrl,
  getHighResDerivativePath
} from '@hub/common/src/media-utils';
import { useCitThemeClient } from '@hub/common-components/src/CitThemeProvider';
import { useQuery } from '@tanstack/react-query';
import { queryFunctions } from '@hub/common/src/query-functions';

declare global {
  interface Window {
    videojs?: typeof videojs;
  }
}

export default function MediaViewer({
  thumbnails,
  enableGallery = true,
  onScreen = enableGallery,
  accountId,
  detailId
}: {
  thumbnails: Media[];
  enableGallery?: boolean;
  // whether the viewer is on screen, for counting views; enableGallery can be off while it still is, when a
  // reference overlay over it holds the one lightgallery
  onScreen?: boolean;
  accountId: string;
  detailId: string;
}) {
  const galleryRef = useRef<LightGalleryType | null>(null);
  const hasCapturedInitialPartialView = useRef(false);

  // lightgallery expects a videojs object to be available in the window
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.videojs) {
      window.videojs = videojs;
    }
  }, []);

  // used to track the current slide index, is ref because we don't want to trigger re-renders
  const currentSlideIndex = useRef<number>(0);

  // used to track the index of the slide that is currently being displayed in the main image, is state because we want to trigger re-renders
  const [displayIndex, setDisplayIndex] = useState(0);

  const citOrigin = useCitOriginClient();

  const { data: accountDetails, isLoading: isLoadingAccountDetails } = useQuery({
    queryKey: ['account-details', { accountId, citOrigin }],
    queryFn: queryFunctions.getAccountDetails
  });

  const { accountTheme } = useCitThemeClient();

  const posthog = usePostHog();

  const { consentLoaded } = useCookieConsentStatus();

  const imageSizing = accountTheme.mediaAspectRatio() === MediaAspectRatio.SQUARE ? 'sq' : 'nt';

  useEffect(() => {
    // off screen (a carousel neighbour, a closed overlay): forget the view, so coming back on screen
    // counts again, as the pageview for that slide does
    if (!onScreen) {
      hasCapturedInitialPartialView.current = false;
      return;
    }

    // PostHogSyncProperties mounts once consent loads and sets the account group before this runs
    if (!consentLoaded || hasCapturedInitialPartialView.current || !thumbnails[displayIndex]) {
      return;
    }

    // capture partial view event for the first thumbnail
    posthog.capture('media_view', {
      detail_id: detailId,

      media_id: thumbnails[displayIndex].uuid,
      media_type: thumbnails[displayIndex].content_type,
      media_name: thumbnails[displayIndex].name,

      full_view: false
    });

    hasCapturedInitialPartialView.current = true;
  }, [consentLoaded, displayIndex, posthog, thumbnails, detailId, onScreen]);

  // controls if audio/video slides are enabled for the account. HUB Pro always has playback, so the
  // flag is only an opt-in for the other origins
  const playBack =
    accountDetails?.customization?.features?.playback || citOrigin === CitOrigin.HUBPRO;

  // controls if download button is enabled for the account
  const download = accountDetails?.customization?.features?.download ?? true;
  const slidesRef = useRef<React.ReactElement[]>([]);

  const handleClickMainImage = () => {
    if (galleryRef.current) {
      galleryRef.current.openGallery(displayIndex);
    }
  };

  const handleSlideChange = useCallback(
    (detail: AfterSlideDetail) => {
      const index = detail.index;

      posthog.capture('media_view', {
        detail_id: detailId,

        media_id: thumbnails[index].uuid,
        media_type: thumbnails[index].content_type,
        media_name: thumbnails[index].name,

        full_view: true
      });

      currentSlideIndex.current = index;
    },
    [posthog, thumbnails, detailId]
  );

  const handleClose = useCallback(() => {
    setDisplayIndex(currentSlideIndex.current);
  }, []);

  const handleInit = useCallback((ref: InitDetail) => {
    galleryRef.current = ref.instance;
  }, []);

  const handleClickThumbnail = (event: React.MouseEvent<HTMLButtonElement>) => {
    const data = event.currentTarget.dataset as { index: string };

    if (!data) {
      return;
    }

    const index = Number(data.index);

    if (index === displayIndex) {
      return;
    }

    const thumbnail = thumbnails[index];

    posthog.capture('media_view', {
      detail_id: detailId,

      media_id: thumbnail.uuid,
      media_type: thumbnail.content_type,
      media_name: thumbnail.name,

      full_view: false
    });

    setDisplayIndex(index);
  };

  // slides with thumbnails and full size images which are displayed inside of the lightgallery when opened
  const slides = useMemo(
    () =>
      thumbnails.map((thumbnail) => {
        if (isPDF(thumbnail)) {
          return (
            // data-iframe='true' is used to open the PDF in an iframe
            <a
              data-download-url={getDownloadUrl(thumbnail, citOrigin, accountDetails)}
              data-iframe='true'
              data-iframe-title={thumbnail.name}
              key={thumbnail.uuid}
              href={getOriginalPDFUrl(thumbnail)}
              data-sub-html={
                thumbnail.name || thumbnail.description
                  ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                  : ''
              }
            >
              <img
                alt={
                  thumbnail.name && thumbnail.description
                    ? `${thumbnail.name}: ${thumbnail.description}`
                    : thumbnail.name || thumbnail.description || ''
                }
                src={getThumbnailPath(thumbnail)}
              />
            </a>
          );
        } else if (isImage(thumbnail)) {
          return (
            <a
              data-download-url={getDownloadUrl(thumbnail, citOrigin, accountDetails)}
              key={thumbnail.uuid}
              data-sub-html={
                thumbnail.name || thumbnail.description
                  ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                  : ''
              }
              href={getHighResDerivativePath(thumbnail, undefined, imageSizing)}
            >
              <img
                alt={
                  thumbnail.name && thumbnail.description
                    ? `${thumbnail.name}: ${thumbnail.description}`
                    : thumbnail.name || thumbnail.description || ''
                }
                src={getThumbnailPath(thumbnail)}
              />
            </a>
          );
        } else if (isVideo(thumbnail)) {
          if (playBack) {
            return (
              <a
                // type will always be application/x-mpegURL for videos
                data-video={JSON.stringify({
                  source: [
                    {
                      src: getDerivativeVideoPath(thumbnail),
                      type: 'application/x-mpegURL'
                    }
                  ]
                })}
                data-download-url={getDownloadUrl(thumbnail, citOrigin, accountDetails)}
                data-poster={getPosterPath(thumbnail)}
                data-sub-html={
                  thumbnail.name || thumbnail.description
                    ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                    : ''
                }
                key={thumbnail.uuid}
              >
                <img
                  src={getThumbnailPath(thumbnail)}
                  alt={
                    thumbnail.name && thumbnail.description
                      ? `${thumbnail.name}: ${thumbnail.description}`
                      : thumbnail.name || thumbnail.description || ''
                  }
                />
              </a>
            );
          } else {
            // display poster image if playback is disabled
            return (
              <a
                data-download-url={getDownloadUrl(thumbnail, citOrigin, accountDetails)}
                key={thumbnail.uuid}
                href={getPosterPath(thumbnail)}
                data-sub-html={
                  '<h1>(This account does not support video playback)</h1>' +
                  (thumbnail.name || thumbnail.description
                    ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                    : '')
                }
              >
                <img
                  alt={
                    thumbnail.name && thumbnail.description
                      ? `${thumbnail.name}: ${thumbnail.description}`
                      : thumbnail.name || thumbnail.description || ''
                  }
                  src={getThumbnailPath(thumbnail)}
                />
              </a>
            );
          }
        } else if (isAudio(thumbnail)) {
          if (playBack) {
            return (
              <a
                // type will always be audio/mp3 for audio
                data-video={JSON.stringify({
                  source: [{ src: getDerivativeAudioPath(thumbnail), type: 'audio/mp3' }]
                })}
                data-poster={getPosterPath(thumbnail)}
                data-download-url={getDownloadUrl(thumbnail, citOrigin, accountDetails)}
                data-sub-html={
                  thumbnail.name || thumbnail.description
                    ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                    : ''
                }
                key={thumbnail.uuid}
              >
                <img
                  src={getThumbnailPath(thumbnail)}
                  alt={
                    thumbnail.name && thumbnail.description
                      ? `${thumbnail.name}: ${thumbnail.description}`
                      : thumbnail.name || thumbnail.description || ''
                  }
                />
              </a>
            );
          } else {
            return (
              // display poster image if playback is disabled
              <a
                data-download-url={getDownloadUrl(thumbnail, citOrigin, accountDetails)}
                key={thumbnail.uuid}
                href={getPosterPath(thumbnail)}
                data-sub-html={
                  '<h1>(This account does not support audio playback)</h1>' +
                  (thumbnail.name || thumbnail.description
                    ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                    : '')
                }
              >
                <img
                  alt={
                    thumbnail.name && thumbnail.description
                      ? `${thumbnail.name}: ${thumbnail.description}`
                      : thumbnail.name || thumbnail.description || ''
                  }
                  src={getThumbnailPath(thumbnail)}
                />
              </a>
            );
          }
        } else {
          // will return a placeholder image for unknown media types
          return (
            <a
              data-download-url={getDownloadUrl(thumbnail, citOrigin, accountDetails)}
              key={thumbnail.uuid}
              data-sub-html={
                thumbnail.name || thumbnail.description
                  ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                  : ''
              }
              href={getHighResDerivativePath(thumbnail, undefined, imageSizing)}
            >
              <img
                alt={
                  thumbnail.name && thumbnail.description
                    ? `${thumbnail.name}: ${thumbnail.description}`
                    : thumbnail.name || thumbnail.description || ''
                }
                src={getThumbnailPath(thumbnail)}
              />
            </a>
          );
        }
      }),
    [thumbnails, playBack, imageSizing, citOrigin, accountDetails]
  );

  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);

  const handleBeforeSlide = useCallback((detail: BeforeSlideDetail) => {
    const instance = galleryRef.current;
    const slide = slidesRef.current[detail.index]?.props as Record<string, unknown> | undefined;

    if (!instance || !slide) {
      return;
    }

    // fix for the bottom caption bar not being added dynamically between slides when you open a slide without
    //  a caption and then navigate to a slide with a caption inside the fullscreen gallery
    const subHtml = (slide['data-sub-html'] as string | undefined) ?? '';
    instance.outer.find('.lg-sub-html').html(subHtml);

    const captionContainer = instance.outer.find(instance.settings.appendSubHtmlTo);

    // when theres no caption add the built-in empty class to the caption container which removes the bottom black bar and remove it otherwise
    if (subHtml === '') {
      captionContainer.addClass('lg-empty-html');
    } else {
      captionContainer.removeClass('lg-empty-html');
    }

    // reserve space for the caption bar
    const { top, bottom } = instance.getMediaContainerPosition();
    instance.$content.css('top', `${top}px`).css('bottom', `${bottom}px`);

    // have to use querySelector as lightGallery has no way of hiding buttons from slide to slide without re-rendering the whole gallery
    const zoomBtn = document.querySelector('.lg-zoom') || document.querySelector('.lg-zoom-in');

    const downloadBtn = document.querySelector('.lg-download');

    if (!zoomBtn || !downloadBtn) {
      return;
    }

    // pdf and audio/video slides should not have a zoom button
    if (slide?.['data-iframe'] || slide?.['data-video']) {
      zoomBtn.classList.add('hidden');
    } else {
      zoomBtn.classList.remove('hidden');
    }

    // pdf slides should not have a download button
    if (slide?.['data-iframe']) {
      downloadBtn.classList.add('hidden');
    } else {
      downloadBtn.classList.remove('hidden');
    }
  }, []);

  const memoizedLightGallery = useMemo(() => {
    if (!enableGallery) {
      return null;
    }

    return (
      <LightGallery
        licenseKey={process.env.NEXT_PUBLIC_LIGHT_GALLERY_LICENSE_KEY || ''}
        onInit={handleInit}
        onAfterSlide={handleSlideChange}
        onBeforeSlide={handleBeforeSlide}
        onBeforeClose={handleClose}
        speed={500}
        plugins={[lgVideo, lgZoom, lgThumbnail, lgFullscreen]}
        videojs={true}
        videojsOptions={{
          controls: true,
          autoplay: false
        }}
        mobileSettings={{
          showCloseIcon: true
        }}
        scale={0.3}
        showZoomInOutIcons={true}
        infiniteZoom={true}
        actualSize={true}
        // hide internal lightbox thumbnails
        elementClassNames='hidden'
        mode='lg-fade'
        closable={true}
        // controls if download button is shown for the entire gallery
        // overrides the data-download-url attribute on the slides
        download={download}
      >
        {slides}
      </LightGallery>
    );
  }, [
    download,
    enableGallery,
    handleClose,
    handleBeforeSlide,
    handleInit,
    handleSlideChange,
    slides
  ]);

  if (isLoadingAccountDetails || !accountDetails) {
    // TODO: need appropriate loading component that fits in here
    return null;
  }

  return (
    <>
      <div className='h-full w-full max-w-full max-h-full flex flex-col justify-between overflow-hidden gap-2'>
        <div
          onClick={handleClickMainImage}
          className={'relative h-[400px] w-full cursor-pointer overflow-hidden lg:h-full'}
        >
          <div className='relative h-full w-full'>
            {(isVideo(thumbnails[displayIndex]) && playBack) ||
            (isAudio(thumbnails[displayIndex]) && playBack) ? (
              <div className='absolute inset-0 z-10 flex items-center justify-center'>
                <div className='flex h-16 w-16 items-center justify-center rounded-full bg-black/50'>
                  <div className='w-0 h-0 border-t-12 border-t-transparent border-l-20 border-l-white border-b-12 border-b-transparent translate-x-[2px]' />
                </div>
              </div>
            ) : (isVideo(thumbnails[displayIndex]) || isAudio(thumbnails[displayIndex])) &&
              !playBack ? (
              <div className='absolute inset-0 z-10 flex items-center justify-center'>
                <div className='relative flex h-16 w-16 items-center justify-center rounded-full bg-black/50'>
                  <div className='w-0 h-0 border-t-12 border-t-transparent border-l-20 border-l-white border-b-12 border-b-transparent translate-x-[2px] opacity-50' />
                  <div className='absolute inset-0 flex items-center justify-center'>
                    <div className='h-1 w-20 origin-center rotate-45 transform bg-white'></div>
                  </div>
                </div>
              </div>
            ) : null}
            <img
              className='h-full w-full object-contain'
              // display poster image for videos/audio
              src={
                isVideo(thumbnails[displayIndex]) || isAudio(thumbnails[displayIndex])
                  ? getPosterPath(thumbnails[displayIndex])
                  : getHighResDerivativePath(thumbnails[displayIndex], undefined, imageSizing)
              }
              alt={
                thumbnails[displayIndex].name && thumbnails[displayIndex].description
                  ? `${thumbnails[displayIndex].name}: ${thumbnails[displayIndex].description}`
                  : thumbnails[displayIndex].name || thumbnails[displayIndex].description || ''
              }
            />
          </div>
        </div>

        {thumbnails[displayIndex].name && thumbnails[displayIndex].description ? (
          <div className='w-full justify-center items-center flex flex-col gap-2'>
            <h5 className='text-center text-xl font-bold line-clamp-1'>
              {thumbnails[displayIndex].name}
            </h5>
            <p className='text-center text-sm line-clamp-2'>
              {thumbnails[displayIndex].description}
            </p>
          </div>
        ) : thumbnails[displayIndex].name || thumbnails[displayIndex].description ? (
          <div className='w-full justify-center items-center flex flex-col gap-2'>
            <p
              className={`text-center line-clamp-2 ${
                thumbnails[displayIndex].name ? 'font-bold text-xl' : 'text-sm'
              }`}
            >
              {thumbnails[displayIndex].name || thumbnails[displayIndex].description}
            </p>
          </div>
        ) : null}

        <div
          className='flex overflow-x-auto gap-1 items-center z-100 h-[135px]'
          // prevents the slide from being dragged when user interacts with the thumbnails
          data-scrollable='true'
        >
          {/* thumbnails which are actually displayed */}
          {thumbnails.map((thumbnail, index) => {
            return (
              <button
                onClick={handleClickThumbnail}
                className={`h-[100px] w-[100px] shrink-0 rounded-lg border-2 transition-all cursor-pointer ${
                  displayIndex === index ? 'border-blue-500' : 'border-transparent'
                }`}
                data-index={index}
                key={thumbnail.uuid}
              >
                <img
                  className='w-full h-full object-cover aspect-square'
                  src={getThumbnailPath(thumbnail)}
                  alt={
                    thumbnail.name && thumbnail.description
                      ? `${thumbnail.name}: ${thumbnail.description}`
                      : thumbnail.name || thumbnail.description || ''
                  }
                />
              </button>
            );
          })}
          {/* only one lightgallery component can be loaded at a time due to videojs needing to have only one instance present */}
          {memoizedLightGallery}
        </div>
      </div>
    </>
  );
}
